import { Component, inject, input, signal, type InputSignal, type WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import { ReportCohortBasedOn, ReportCohortGgrType, ReportCohortPeriod } from '../../../../../interfaces/reports';
import { ReportsService } from '../../../../../services/reports.service';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { finalize } from 'rxjs';
import { AutoCompleteModule, type AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { colorByPercentage } from '../../../../../shared/functions/color-by-percentage';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CardHeader } from '../../../../../shared/components/card/card-header/card-header';
import { Card } from '../../../../../shared/components/card/card/card';
import { EmptyChart } from '../../../../../shared/components/empty-chart/empty-chart';
import { ReportCohortPeriodPipe } from '../../../../../shared/pipes/report-cohort-period-pipe';
import { ReportCohortGgrTypePipe } from "../../../../../shared/pipes/report-cohort-ggr-type-pipe";
import { SelectModule } from 'primeng/select';
import { ReportCohortBasedByPipe } from '../../../../../shared/pipes/report-cohort-based-by-pipe';
import { MenuModule } from 'primeng/menu';
import type { MenuItem } from 'primeng/api';
import saveAs from 'file-saver';

@Component({
  selector: 'app-cohort-ggr',
  imports: [
    CommonModule,
    FormsModule,
    Card,
    CardHeader,
    TableModule,
    SkeletonModule,
    ProgressSpinnerModule,
    DatePickerModule,
    ReactiveFormsModule,
    FloatLabelModule,
    ButtonModule,
    AutoCompleteModule,
    SelectButtonModule,
    ReportCohortPeriodPipe,
    TooltipModule,
    EmptyChart,
    ReportCohortGgrTypePipe,
    SelectModule,
    ReportCohortBasedByPipe,
    MenuModule
],
  templateUrl: './cohort-ggr.html',
  styleUrl: './cohort-ggr.css',
})
export class CohortGgr {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _reportsService: ReportsService = inject(ReportsService);

  public exportLoading: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly reportCohortPeriod: typeof ReportCohortPeriod = ReportCohortPeriod;
  protected readonly reportCohortGgrType: typeof ReportCohortGgrType = ReportCohortGgrType;
  protected readonly reportCohortBasedOn: typeof ReportCohortBasedOn = ReportCohortBasedOn;

  public financialBrandUtmSourceData: InputSignal<any> = input<any>(null);

  public financialCohortGgrShow: ReportCohortPeriod = ReportCohortPeriod.DAILY;
  public financialCohortGgrTypeShow: ReportCohortGgrType = ReportCohortGgrType.TOTAL;

  public financialCohortGgrUtmFilter: WritableSignal<string[]> = signal<string[]>([]);
  public financialCohortGgrForm: FormGroup = this._fb.group({
    utm_source: [null],
    based_by: [ReportCohortBasedOn.CREATED],
    date_range_daily: [[dayjs().startOf('month').toDate(), dayjs().toDate()]],
    date_range_monthly: [[dayjs().startOf('year').toDate(), dayjs().toDate()]],
  });

  public financialCohortGgrDailyData: WritableSignal<any> = signal<any>(null);
  public financialCohortGgrDailyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortGgrDailyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public financialCohortGgrMonthlyData: WritableSignal<any> = signal<any>(null);
  public financialCohortGgrMonthlyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortGgrMonthlyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public financialCohortGgrCasinoDailyData: WritableSignal<any> = signal<any>(null);
  public financialCohortGgrCasinoDailyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortGgrCasinoDailyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public financialCohortGgrCasinoMonthlyData: WritableSignal<any> = signal<any>(null);
  public financialCohortGgrCasinoMonthlyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortGgrCasinoMonthlyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public financialCohortGgrSportsDailyData: WritableSignal<any> = signal<any>(null);
  public financialCohortGgrSportsDailyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortGgrSportsDailyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public financialCohortGgrSportsMonthlyData: WritableSignal<any> = signal<any>(null);
  public financialCohortGgrSportsMonthlyFirstRequest: WritableSignal<boolean> = signal<boolean>(true);
  public financialCohortGgrSportsMonthlyLoading: WritableSignal<boolean> = signal<boolean>(false);

  public exportMenuItems: MenuItem[] = [
    {
      label: 'PDF',
      icon: 'fa-solid fa-file-pdf',
      action: () => this.export()
    },
    {
      label: 'CSV',
      icon: 'fa-solid fa-file-csv',
      action: () => this.exportCsv()
    }
  ];

  get tableId(): string {
    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.TOTAL) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        return 'financialCohortGgrDailyTable';
      }

      return 'financialCohortGgrMonthlyTable';
    }

    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.CASINO) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        return 'financialCohortGgrDailyTable';
      }

      return 'financialCohortGgrMonthlyTable';
    }

    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.SPORTS) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        return 'financialCohortGgrDailyTable';
      }

      return 'financialCohortGgrMonthlyTable';
    }

    return '';
  }

  public filterUtmMethodCompleteMethod(event: AutoCompleteCompleteEvent): void {
      this.financialCohortGgrUtmFilter.set(
        this.financialBrandUtmSourceData().utm_sources
          .filter((item: string) => item.toLowerCase().includes(event.query.toLowerCase()))
      )
  }

  public fromPaymentFiltersForm(form: FormGroup, key: string): string {

    if (key === 'start_date_daily') {
      return dayjs(form.get('date_range_daily')?.value[0]).format('YYYY-MM-DD');
    }

    if (key === 'end_date_daily') {
      return dayjs(form.get('date_range_daily')?.value[1]).format('YYYY-MM-DD');
    }

    if (key === 'start_date_monthly') {
      return dayjs(form.get('date_range_monthly')?.value[0]).format('YYYY-MM-DD');
    }

    if (key === 'end_date_monthly') {
      return dayjs(form.get('date_range_monthly')?.value[1]).format('YYYY-MM-DD');
    }

    return form.get(key)?.value;
  }

  public getFinancialCohortGgr(force: boolean = false): void {

    if (!force) {
      if (this.financialCohortGgrTypeShow === ReportCohortGgrType.TOTAL) {
        if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY
          && this.financialCohortGgrDailyData()) {
          return;
        }

        if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY
          && this.financialCohortGgrMonthlyData()
        ) {
          return;
        }
      }

      if (this.financialCohortGgrTypeShow === ReportCohortGgrType.CASINO) {
        if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY
          && this.financialCohortGgrCasinoDailyData()) {
          return;
        }

        if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY
          && this.financialCohortGgrCasinoMonthlyData()
        ) {
          return;
        }
      }

      if (this.financialCohortGgrTypeShow === ReportCohortGgrType.SPORTS) {
        if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY
          && this.financialCohortGgrSportsDailyData()) {
          return;
        }

        if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY
          && this.financialCohortGgrSportsMonthlyData()
        ) {
          return;
        }
      }
    }

    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.TOTAL) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        this.getFinancialCohortGgrDaily();
      }

      if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY) {
        this.getFinancialCohortGgrMonthly();
      }
    }

    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.CASINO) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        this.getFinancialCohortGgrCasinoDaily();
      }

      if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY) {
        this.getFinancialCohortGgrCasinoMonthly();
      }
    }

    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.SPORTS) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        this.getFinancialCohortGgrSportsDaily();
      }

      if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY) {
        this.getFinancialCohortGgrSportsMonthly();
      }
    }
  }

  public getFinancialCohortGgrDaily(): void {
    this.financialCohortGgrDailyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_daily'),
    };

    this.financialCohortGgrDailyData.set(null);
    this.financialCohortGgrDailyFirstRequest.set(false);

    this._reportsService.financialCohortGgrDaily(filters)
      .pipe(finalize(() => this.financialCohortGgrDailyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortGgrDailyData.set(response.data);
          console.log(this.financialCohortGgrDailyData());
        }
      })
  }

  public getFinancialCohortGgrMonthly(): void {
    this.financialCohortGgrMonthlyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_monthly'),
    };

    this.financialCohortGgrMonthlyData.set(null);
    this.financialCohortGgrMonthlyFirstRequest.set(false);

    this._reportsService.financialCohortGgrMonthly(filters)
      .pipe(finalize(() => this.financialCohortGgrMonthlyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortGgrMonthlyData.set(response.data);
        }
      })
  }

  public getFinancialCohortGgrCasinoDaily(): void {
    this.financialCohortGgrCasinoDailyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_daily'),
    };

    this.financialCohortGgrCasinoDailyData.set(null);
    this.financialCohortGgrCasinoDailyFirstRequest.set(false);

    this._reportsService.financialCohortGgrCasinoDaily(filters)
      .pipe(finalize(() => this.financialCohortGgrCasinoDailyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortGgrCasinoDailyData.set(response.data);
        }
      })
  }

  public getFinancialCohortGgrCasinoMonthly(): void {
    this.financialCohortGgrCasinoMonthlyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_monthly'),
    };

    this.financialCohortGgrCasinoMonthlyData.set(null);
    this.financialCohortGgrCasinoMonthlyFirstRequest.set(false);

    this._reportsService.financialCohortGgrCasinoMonthly(filters)
      .pipe(finalize(() => this.financialCohortGgrCasinoMonthlyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortGgrCasinoMonthlyData.set(response.data);
        }
      })
  }

  public getFinancialCohortGgrSportsDaily(): void {
    this.financialCohortGgrSportsDailyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_daily'),
    };

    this.financialCohortGgrSportsDailyData.set(null);
    this.financialCohortGgrSportsDailyFirstRequest.set(false);

    this._reportsService.financialCohortGgrSportsDaily(filters)
      .pipe(finalize(() => this.financialCohortGgrSportsDailyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortGgrSportsDailyData.set(response.data);
        }
      })
  }

  public getFinancialCohortGgrSportsMonthly(): void {
    this.financialCohortGgrSportsMonthlyLoading.set(true);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_monthly'),
    };

    this.financialCohortGgrSportsMonthlyData.set(null);
    this.financialCohortGgrSportsMonthlyFirstRequest.set(false);

    this._reportsService.financialCohortGgrSportsMonthly(filters)
      .pipe(finalize(() => this.financialCohortGgrSportsMonthlyLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.financialCohortGgrSportsMonthlyData.set(response.data);
        }
      })
  }

  public color(percentage: number) {
    return colorByPercentage(percentage);
  }

  public exportCsv(): void {

    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.TOTAL) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        this.exportCsvGgrDaily();
      }

      if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY) {
        this.exportCsvGgrMonthly();
      }
    }

    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.CASINO) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        this.exportCsvGgrCasinoDaily();
      }

      if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY) {
        this.exportCsvGgrCasinoMonthly();
      }
    }

    if (this.financialCohortGgrTypeShow === ReportCohortGgrType.SPORTS) {
      if (this.financialCohortGgrShow === ReportCohortPeriod.DAILY) {
        this.exportCsvGgrSportsDaily();
      }

      if (this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY) {
        this.exportCsvGgrSportsMonthly();
      }
    }
  }

  public exportCsvGgrDaily(): void {
    this.exportLoading.update(value => !value);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_daily'),
    };

    this._reportsService.financialCohortGgrDailyCsv(filters)
      .pipe(finalize(() => this.exportLoading.update(value => !value)))
      .subscribe({
        next: (blob) => {
          console.log({blob})
          const fileName = `cohort-ggr-diario-${new Date().toLocaleString()}.csv`;
          saveAs(blob, fileName);
        },
      })
  }

  public exportCsvGgrMonthly(): void {
    this.exportLoading.update(value => !value);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_monthly'),
    };

    this._reportsService.financialCohortGgrMonthlyCsv(filters)
      .pipe(finalize(() => this.exportLoading.update(value => !value)))
      .subscribe({
        next: (blob) => {
          console.log({blob})
          const fileName = `cohort-ggr-mensal-${new Date().toLocaleString()}.csv`;
          saveAs(blob, fileName);
        },
      })
  }

  public exportCsvGgrCasinoDaily(): void {
    this.exportLoading.update(value => !value);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_daily'),
    };

    this._reportsService.financialCohortGgrCasinoDailyCsv(filters)
      .pipe(finalize(() => this.exportLoading.update(value => !value)))
      .subscribe({
        next: (blob) => {
          console.log({blob})
          const fileName = `cohort-ggr-cassino-diario-${new Date().toLocaleString()}.csv`;
          saveAs(blob, fileName);
        },
      })
  }

  public exportCsvGgrCasinoMonthly(): void {
    this.exportLoading.update(value => !value);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_monthly'),
    };

    this._reportsService.financialCohortGgrCasinoMonthlyCsv(filters)
      .pipe(finalize(() => this.exportLoading.update(value => !value)))
      .subscribe({
        next: (blob) => {
          console.log({blob})
          const fileName = `cohort-ggr-cassino-mensal-${new Date().toLocaleString()}.csv`;
          saveAs(blob, fileName);
        },
      })
  }

  public exportCsvGgrSportsDaily(): void {
    this.exportLoading.update(value => !value);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_daily'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_daily'),
    };

    this._reportsService.financialCohortGgrSportsDailyCsv(filters)
      .pipe(finalize(() => this.exportLoading.update(value => !value)))
      .subscribe({
        next: (blob) => {
          console.log({blob})
          const fileName = `cohort-ggr-esporte-diario-${new Date().toLocaleString()}.csv`;
          saveAs(blob, fileName);
        },
      })
  }

  public exportCsvGgrSportsMonthly(): void {
    this.exportLoading.update(value => !value);

    const filters = {
      based_by: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'based_by'),
      utm_source: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'utm_source'),
      start_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_monthly'),
      end_date: this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_monthly'),
    };

    this._reportsService.financialCohortGgrSportsMonthlyCsv(filters)
      .pipe(finalize(() => this.exportLoading.update(value => !value)))
      .subscribe({
        next: (blob) => {
          console.log({blob})
          const fileName = `cohort-ggr-esporte-mensal-${new Date().toLocaleString()}.csv`;
          saveAs(blob, fileName);
        },
      })
  }

  public export() {
    this.exportLoading.set(true);
    const el = document.getElementById(this.tableId);

    const period = {
      start: this.financialCohortGgrShow === ReportCohortPeriod.DAILY ?
        this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_daily') :
        this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'start_date_monthly'),
      end: this.financialCohortGgrShow === ReportCohortPeriod.DAILY ?
        this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_daily') :
        this.fromPaymentFiltersForm(this.financialCohortGgrForm, 'end_date_monthly'),
    }

    if (el) {
      setTimeout(async () => {
        html2canvas(el, {
          scrollX: 0,
          scrollY: 0,
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
          width: el.scrollWidth,
          height: el.scrollHeight
        }).then(canvas => {

          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('p');
          const imgWidth = 200;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          pdf.setFontSize(12);
          pdf.text("Cohort - GGR", 5, 10);
          pdf.text(`${dayjs(period.start).format('DD/MM/YYYY')} - ${dayjs(period.end).format('DD/MM/YYYY')}`, 205, 10, { align: 'right' });
          pdf.addImage(imgData, 'PNG', 5, 15, imgWidth, imgHeight);
          pdf.save(`${this.tableId}-${dayjs().format('YYYY-MM-DD-HH:mm:ss')}.pdf`);

          this.exportLoading.set(false);
        }).catch(error => {
          this.exportLoading.set(false);
          console.error('Erro ao gerar PDF:', error);
        });
      });
    }
  }

  public showExportButton(): boolean {
    return this.financialCohortGgrTypeShow === ReportCohortGgrType.TOTAL && this.financialCohortGgrShow === ReportCohortPeriod.DAILY && this.financialCohortGgrDailyData() ||
      this.financialCohortGgrTypeShow === ReportCohortGgrType.TOTAL && this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY && this.financialCohortGgrMonthlyData() ||
      this.financialCohortGgrTypeShow === ReportCohortGgrType.CASINO && this.financialCohortGgrShow === ReportCohortPeriod.DAILY && this.financialCohortGgrCasinoDailyData() ||
      this.financialCohortGgrTypeShow === ReportCohortGgrType.CASINO && this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY && this.financialCohortGgrCasinoMonthlyData() ||
      this.financialCohortGgrTypeShow === ReportCohortGgrType.SPORTS && this.financialCohortGgrShow === ReportCohortPeriod.DAILY && this.financialCohortGgrSportsDailyData() ||
      this.financialCohortGgrTypeShow === ReportCohortGgrType.SPORTS && this.financialCohortGgrShow === ReportCohortPeriod.MONTHLY && this.financialCohortGgrSportsMonthlyData();
  }
}
